import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Sparkles } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { SEO } from '../seo/SEO';

const DEFAULT_WHATSAPP_NUMBER = '212600000000';

function sanitizeNumber(raw: string): string {
  return raw.replace(/[^\d]/g, '');
}

/**
 * Soft-launch placeholder. Rendered for every public route while
 * {@code VITE_UNDER_CONSTRUCTION=true}. Admin paths bypass this page via
 * the gate in {@code App.tsx}; the gate also honours a one-shot URL
 * param {@code ?preview=KEY} so an admin can preview the real catalog
 * from any device without touching the env build.
 *
 * Intentionally self-contained: doesn't depend on cart/auth context so
 * it can also be the very first thing visitors see if other providers
 * fail to mount for any reason.
 */
export function UnderConstructionPage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const envNumber = import.meta.env.VITE_WHATSAPP_NUMBER as string | undefined;
  const number = sanitizeNumber(envNumber || DEFAULT_WHATSAPP_NUMBER);
  const whatsappMessage = t('underConstruction.whatsapp.message');
  const whatsappHref = `https://wa.me/${number}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div
      className={`min-h-screen bg-ivory flex flex-col ${
        i18n.language === 'ar' ? 'font-arabic' : ''
      }`}
    >
      <SEO
        titleKey="seo.underConstruction.title"
        descriptionKey="seo.underConstruction.description"
        noindex
      />
      <header className="border-b border-linen bg-paper/60 backdrop-blur-sm">
        <div className="max-w-editorial mx-auto px-6 sm:px-10 py-5 flex items-center justify-between gap-6">
          <Link to="/" className="group flex items-baseline gap-3">
            <span className="editorial-number text-2xl leading-none">N°</span>
            <span className="font-display text-2xl tracking-wide leading-none">
              {t('header.title')}
            </span>
          </Link>
          <LanguageSelector />
        </div>
      </header>

      <main className="flex-1 grain-overlay flex items-center">
        <div className="max-w-editorial mx-auto px-6 sm:px-10 py-20 sm:py-32 w-full grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-2">
            <span className="editorial-number text-5xl leading-none">N°00</span>
            <div className="mt-3 editorial-label">{t('underConstruction.kicker')}</div>
          </div>

          <div className="col-span-12 md:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 border border-brass/40 text-brass">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              <span className="editorial-label">{t('underConstruction.badge')}</span>
            </div>

            <h1 className="font-display text-[clamp(2.5rem,7vw,5.5rem)] leading-[0.95] tracking-tight text-ink mb-6">
              <span className="block">
                {t('underConstruction.title.line1')}
              </span>
              <span className="block italic text-brass">
                {t('underConstruction.title.line2')}
              </span>
            </h1>

            <p className="font-body text-base sm:text-lg text-ink-soft leading-relaxed max-w-prose mb-10">
              {t('underConstruction.subtitle')}
            </p>

            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-4 transition-colors duration-300"
              style={{ backgroundColor: '#25D366', color: '#FFFFFF' }}
            >
              <MessageCircle className="w-4 h-4" />
              <span className="editorial-label" style={{ color: '#FFFFFF' }}>
                {t('underConstruction.contactCta')}
              </span>
            </a>

            <p className="mt-4 editorial-label text-ink-soft">
              {t('underConstruction.contactHint')}
            </p>
          </div>

          <div className="col-span-12 md:col-span-3 md:border-s md:border-linen md:ps-8">
            <div className="editorial-rule mb-4" />
            <span className="editorial-label block mb-3 text-ink-soft">
              {t('underConstruction.expected.label')}
            </span>
            <p className="font-display text-2xl italic text-ink mb-6">
              {t('underConstruction.expected.value')}
            </p>

            <span className="editorial-label block mb-3 text-ink-soft">
              {t('underConstruction.workshop.label')}
            </span>
            <p className="font-body text-sm text-ink-soft leading-relaxed">
              {t('underConstruction.workshop.value')}
            </p>
          </div>
        </div>
      </main>

      <footer className="border-t border-linen bg-paper/60">
        <div className="max-w-editorial mx-auto px-6 sm:px-10 py-6 text-center">
          <span className="editorial-label text-ink-soft">
            {t('underConstruction.footer', { year: new Date().getFullYear() })}
          </span>
        </div>
      </footer>
    </div>
  );
}
